<template>
  <b-card class="shadow">
    <div class="mb-4 text-center" v-if="finished">
      <h3>{{ $t('auth.verify-email.success-title') }}</h3>
      <p>{{ $t('auth.verify-email.success-can-close') }}</p>
    </div>
    <div class="mb-4 text-center" v-else>
      <h3>{{ $t('auth.verify-email.title') }}</h3>
      <!-- eslint-disable-next-line vue-i18n/no-missing-keys -->
      <p v-if="$te('auth.verify-email.description')">{{ $t('auth.verify-email.description') }}</p>
    </div>

    <b-alert v-if="err" class="mb-4" show variant="danger">
      {{ (err.extensions ? err.extensions.message : null) || err.user_message || err.message || err }}
    </b-alert>

    <form method="POST" @submit.prevent="submitConfirmationCode" v-if="!finished">
      <b-form-group>
        <b-input-group>
          <b-form-input autocomplete="email email-address" id="emailAddress" type="email" :placeholder="$t('auth.placeholders.emailAddress')"
            :disabled="Boolean(emailAddress)" required v-model="form.emailAddress"/>
          <b-input-group-append v-if="emailAddress">
            <b-btn class="email-change-btn text-dark" @click="() => $emit('setEmailAddress', null)" tabindex="-1" variant="transparent">
              <font-awesome-icon icon="pencil-alt"/>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group>
        <b-form-input id="code" :placeholder="$t('auth.placeholders.confirmationCode')" required type="text" v-model="form.confirmationCode"/>
      </b-form-group>

      <b-form-group>
        <b-btn class="w-100" :disabled="working" size="md" type="submit" variant="primary">
          <template v-if="working">{{ $t('ctas.verifying') }}</template>
          <template v-else>{{ $t('ctas.verify') }}</template>
        </b-btn>
      </b-form-group>
    </form>

    <div v-if="!finished && !working" class="d-flex flex-row justify-content-center align-items-center">
      <p class="mb-0"><a href="#" @click.prevent="resendConfirmationCode">{{ $t('auth.verify-email.resend') }}</a></p>
    </div>
  </b-card>
</template>

<script>
import base64 from 'base-64';
import authMixin from '@src/mixins/auth';
import AuthTriggerChallengeMutation from '@src/graphql/mutations/AuthTriggerChallenge.gql';
import AuthRespondToChallengeMutation from '@src/graphql/mutations/AuthRespondToChallenge.gql';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    company: Object,
    emailAddress: {
      type: String,
      default: () => null,
    },
  },
  mixins: [
    authMixin,
  ],
  data: () => ({
    err: null,
    form: {},
    finished: false,
    working: false,
  }),
  created() {
    this.form.emailAddress = this.emailAddress;
  },
  mounted() {
    const { encodedEmail, code } = this.$route.params || {};

    if (encodedEmail && code) {
      const { encodedEmail, code } = this.$route.params;
      this.form.emailAddress = base64.decode(encodedEmail);
      this.form.confirmationCode = code;

      this.$emit('setEmailAddress', this.form.emailAddress);
      this.submitConfirmationCode();
    }

    const firstInput = this.$el.querySelector('input:not([disabled]):first-child');
    firstInput && firstInput.focus();
  },
  metaInfo() {
    return {
      title: this.$t('auth.verify-email.title'),
    };
  },
  computed: {
    ...mapRoot([ 'isLoggedIn', 'isDemoSite' ]),
  },
  methods: {
    async resendConfirmationCode() {
      try {
        this.err = null;

        const { emailAddress } = this.form;
        this.$emit('setEmailAddress', emailAddress);

        const { errors } = await this.$apollo.mutate({
          mutation: AuthTriggerChallengeMutation,
          variables: {
            challenge: {
              emailAddress,
              challenge: 'VERIFY_ACCOUNT',
              contextData: this.getCognitoContextData(emailAddress),
            },
          },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));
          this.$notification({
            title: this.$t('general.errored'),
            message: this.$t('auth.verify-email.code-resent-errored'),
            toaster: 'b-toaster-top-center',
            variant: 'danger',
          });
        } else {
          this.$notification({
            title: this.$t('general.success'),
            message: this.$t('auth.verify-email.code-resent-success'),
            toaster: 'b-toaster-top-center',
            variant: 'success',
          });
        }
      } catch (err) {
        this.err = err;
      }
    },
    async submitConfirmationCode() {
      try {
        this.finished = false;
        this.working = true;

        const { emailAddress, confirmationCode } = this.form;
        this.$emit('setEmailAddress', emailAddress);

        this.$assert(confirmationCode, new Error('Please enter a confirmation code'));

        const { data, errors } = await this.$apollo.mutate({
          mutation: AuthRespondToChallengeMutation,
          variables: {
            challenge: {
              challenge: 'VERIFY_ACCOUNT',
              emailAddress,
              parameters: [
                { key: 'CONFIRMATION_CODE', value: `${confirmationCode}`.trim() },
              ],
              contextData: this.getCognitoContextData(emailAddress),
            },
          },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('registerVerify', err));
          if (this.$findApolloError(errors, 'extensions.code', 'USER_ALREADY_CONFIRMED')) {
            if (this.isLoggedIn) {
              // If the user has already been confirmed, and we're logged-in
              await this.afterAuthenticated(this.nextRoute);
            } else {
              // Otherwise, ask to login
              this.$notification({
                title: this.$t('auth.verify-email.success-title'),
                message: this.$t('auth.verify-email.success-login-prompt'),
                variant: 'success',
              });

              const { query } = this.$route;
              this.$router.replace({ name: 'Login', query });
            }
            return;
          } else {
            throw errors.shift();
          }
        }

        // If we're already logged-in, accept/reject the request and continue with the current account
        // Otherwise, take the resulting authentication state and login

        if (!this.isLoggedIn) {
          const { respondToCognitoChallenge } = data;
          this.$assert(respondToCognitoChallenge && respondToCognitoChallenge.token, 401, new Error('Failed to login'));
          await this.updateAuthResponse(this.$apollo.getClient(), respondToCognitoChallenge);
          this.$assert(this.isLoggedIn, 401, new Error('Please login to continue'));
        }

        this.finished = true;
      } catch (err) {
        this.err = err;
      } finally {
        this.working = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Auth/RegisterVerify.vue